import React from 'react'
import { connect } from 'react-redux'

import config from 'config'
import LoadingStatus from 'state/enums/LoadingStatus'
import { setIsLoaded } from 'state/iframe'
import { RootState } from 'state/store'

import './LegacyFrame.css'

import IFrameSync, { createIFrameSync } from './IFrameSync'

export type LegacyFrameProps = {
    url: string
    pathname: string
    setIsLoaded?: () => void
    iFrameStatus: LoadingStatus
}

type LegacyFrameState = { src: string }

function mapStateToProps(state: RootState) {
    return {
        iFrameStatus: state.iframe.status,
    }
}

const mapDispatchToProps = { setIsLoaded }

class LegacyFrame extends React.PureComponent<
    LegacyFrameProps,
    LegacyFrameState
> {
    ref = React.createRef<HTMLIFrameElement>()

    iFrameSync: IFrameSync | null = null

    initialPathname?: string

    public static defaultProps = {
        setIsLoaded: () => null,
    }

    constructor(props: LegacyFrameProps) {
        super(props)

        this.initialPathname = props.pathname

        this.state = {
            src: '',
        }
    }

    componentDidMount() {
        if (!this.iFrameSync) {
            const { setIsLoaded: setIframeIsLoaded } = this.props

            this.iFrameSync = createIFrameSync(
                window,
                this.ref.current,
                config.LEGACY_APP_URL,
                setIframeIsLoaded as () => null,
            )

            this.setState({ src: this.getSourceUrl() })
            this.updateHash()
        }
    }

    async componentDidUpdate(prevProps: LegacyFrameProps) {
        const { pathname, iFrameStatus } = this.props

        const iFrameHasLoaded =
            prevProps.iFrameStatus === LoadingStatus.LOADING &&
            iFrameStatus === LoadingStatus.LOADED

        /* istanbul ignore else */
        if (pathname !== prevProps.pathname || iFrameHasLoaded) {
            this.updateHash()
        }
    }

    getStyles() {
        const display = this.shouldShowFrame() ? 'block' : 'none'

        return { display }
    }

    getSourceUrl() {
        const { initialPathname } = this
        const { url } = this.props
        const queryParams = this.getForwardedQueryParams()

        if (this.shouldShowFrame()) {
            return `${url}/index.html${queryParams}#${initialPathname}`
        }

        return `${url}/index.html${queryParams}`
    }

    /**
     * The web-app uses a HashRouter, so query params may appear in the hash
     * portion of the URL (e.g. `/#/!/partner/lightspeed?mockLightspeedBridge=1`).
     *
     * This method always forwards the normal querystring, and only forwards
     * supported hash-based params for local development helpers when running
     * on localhost. It is not intended to broadly forward hash query params
     * in non-local environments.
     */
    getForwardedQueryParams() {
        // NOTE:
        // - Always forward the normal querystring (window.location.search).
        // - Only forward HashRouter query params when running locally.
        //
        // This behavior exists solely to support local testing with the legacy app's
        // dev helpers (e.g. `mockLightspeedBridge=1`). It intentionally does not run
        // in non-local environments.
        try {
            const params = new URLSearchParams(window.location.search || '')

            if (this.isRunningLocally()) {
                this.forwardLocalTestingParamsFromHash(params)
            }

            const serialized = params.toString()

            return serialized ? `?${serialized}` : ''
        } catch {
            return window.location.search || ''
        }
    }

    private isRunningLocally() {
        // This app is considered "local" when it's accessed via localhost.
        // (We intentionally keep this check conservative.)
        const hostname = window.location.hostname

        return hostname === 'localhost' || hostname === '127.0.0.1'
    }

    private forwardLocalTestingParamsFromHash(params: URLSearchParams) {
        // Local-only: support the legacy app's dev helper used when testing the
        // Lightspeed partner flow.
        const KEY = 'mockLightspeedBridge'
        const ENABLED = '1'

        if (params.get(KEY) === ENABLED) return

        const hash = window.location.hash || ''
        const hashQueryIndex = hash.indexOf('?')

        const hashQuery =
            hashQueryIndex >= 0 ? hash.slice(hashQueryIndex + 1) : ''

        const hashParams = new URLSearchParams(hashQuery)

        if (hashParams.get(KEY) === ENABLED) {
            params.set(KEY, ENABLED)
        }
    }

    shouldShowFrame() {
        const { pathname } = this.props

        return pathname?.[0] === '!'
    }

    updateHash() {
        const { pathname } = this.props

        if (this.shouldShowFrame()) {
            /**
             * using setTimeout to put this task in the end
             * of Event Loop Stack to assure that the iframe is
             * fully loaded before emit a postMessage
             */
            setTimeout(() => this.iFrameSync?.setHash(`#${pathname}`), 0)
        }
    }

    render() {
        const { src } = this.state

        return (
            <iframe
                className="LegacyFrame"
                style={this.getStyles()}
                title="Legacy dealer app"
                src={src}
                ref={this.ref}
            />
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(LegacyFrame)
